<template>
    <v-card class="preview-container">
        <!-- Sticky Toolbar -->
        <v-toolbar dark color="primary" class="preview-toolbar">
            <v-btn icon @click="closeDialog" title="Close window">
                <v-icon>close</v-icon>
            </v-btn>
            <v-toolbar-title>{{ selectedFile.name }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="download" title="Download file">
                <v-icon>cloud_download</v-icon>
            </v-btn>
        </v-toolbar>

        <!-- Scrollable Preview Area -->
        <v-card-text class="preview-body">
            <!-- LOADING INDICATOR -->
            <div v-if="!iframeReady" class="loading-indicator">
                <v-progress-circular indeterminate color="primary" size="40" />
            </div>
            
            <div v-if="iframeLoadFailed" class="error-text" style="color: red; margin-top: 10px;">
                Document viewer has encountered an issue while loading the document. Please download the file instead by clicking the download icon.<br>
                <v-btn @click="download" title="Download file">
                    <v-icon>cloud_download</v-icon> Download
                </v-btn>
            </div>

            <!-- PDF -->
            <iframe
                v-if="iframeReady && isPdf(selectedFile) && !isMobile"
                :src="selectedFile.url"
                :key="iframeKey"
                class="pdf-frame"
            ></iframe>

            <iframe
                v-if="iframeReady && isPdf(selectedFile) && isMobile"
                :src="googleViewerUrl(selectedFile.url)"
                :key="iframeKey"
                class="pdf-frame"
                frameborder="0"
            ></iframe>

            <!-- Image -->
            <img
                v-if="isImage(selectedFile)"
                :src="selectedFile.url"
                class="image-file"
            />

            <!-- Word / Excel / PowerPoint or Others -->
            <iframe
                v-if="iframeReady && isOfficeDoc(selectedFile)"
                :src="googleViewerUrl(selectedFile.url)"
                :key="iframeKey"
                class="doc-frame"
                frameborder="0"
            ></iframe>
        </v-card-text>
    </v-card>
</template>
<script>
export default {
    data() {
        return {
            selectedFile: {},
            iframeKey: 0,
            iframeReady: false,
            retryCount: 0,
            maxRetries: 3,
            iframeLoadFailed: false, // <-- controls error message
        };
    },
    computed: {
        isMobile() {
            return typeof window !== 'undefined' && window.innerWidth < 600;
        },
    },
    methods: {
        closeDialog() {
            this.$emit("update:dialogPreview", false);
        },
        setParams(file) {
            this.selectedFile = file;
            this.iframeReady = false;
            this.retryCount = 0; // reset retry count
            this.iframeKey++; // reset key to reload iframe
            setTimeout(() => {
                this.iframeReady = true;
                if (this.isPdf(file) || this.isOfficeDoc(file)) {
                    this.conditionalPdfIFrameReloadTimeout();
                }
            }, 200);
        },
        isPdf(file) {
            return file.type && file.type.toLowerCase().includes("pdf");
        },
        isImage(file) {
            return file.type && file.type.toLowerCase().includes("image");
        },
        isWordDoc(file) {
            return file.type && file.type.toLowerCase().includes("worddocument");
        },
        isOfficeDoc(file) {
            if (file.type) {
                const type = file.type.toLowerCase();
                return (
                    type.includes("worddocument") ||
                    type.includes("excel") ||
                    type.includes("presentation") ||
                    file.name.match(/\.(docx?|xlsx?|pptx?)$/i)
                )
            }
        },
        googleViewerUrl(url) {
            const bustCache = Math.random()
            
            //this url is also working -> https://docs.google.com/viewerng/viewer
            //return `https://docs.google.com/viewerng/viewer?url=${encodeURIComponent(url)}&embedded=true&t=${bustCache}`;
            
            return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true&t=${bustCache}`;
        },
        conditionalPdfIFrameReloadTimeout() {
            setTimeout(() => {
            const iframe = document.querySelector('.pdf-frame') || document.querySelector('.doc-frame');
            if (
                iframe &&
                iframe.contentDocument &&
                iframe.contentDocument.body &&
                iframe.contentDocument.body.innerText.includes("We’re sorry. You can’t access this item because it is in violation of our Terms of Service.")
            ) {
                if (this.retryCount < this.maxRetries) {
                this.retryCount++;
                this.iframeReady = false;
                this.iframeKey++; // force iframe reload
                setTimeout(() => {
                    this.iframeReady = true;
                    this.conditionalPdfIFrameReloadTimeout(); // retry
                }, 200);
                } else {
                    this.iframeLoadFailed = true; // Show fallback message
                }
            }
            }, 1000);
        },
        async download() {
            try {
                const response = await fetch(this.selectedFile.downloadUrl, {
                    method: "GET",
                    mode: "cors",
                });

                if (!response.ok) throw new Error("Failed to download file");

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = this.selectedFile.name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);
            } catch (error) {
                alert("Could not download file. Please try again.");
            }
        },
    },
};
</script>
<style scoped>
.preview-container {
    height: 100vh !important;
    display: flex !important;
    flex-direction: column !important;
}
.preview-toolbar {
    position: sticky;
    top: 0;
    z-index: 10;
}
.preview-body {
    overflow: auto !important;
    flex: 1 !important;
    padding: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: center;
}
.image-file {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100vh;
    display: block;
    margin: auto;
    object-fit: contain;
}
.pdf-frame,
.doc-frame {
    width: 100%;
    height: calc(100vh - 64px);
    border: none;
}
@media (max-width: 768px) {
    .pdf-frame,
    .doc-frame,
    .image-file {
        height: 90vh !important;
    }
}
.loading-indicator {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80vh;
}
</style>